import { Component, EventEmitter, Input, Output, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { Meter, ConsentStatus, ShareInfo } from '../../../models/meter.model';
import { UtilService } from '../../../services/util.service';
import { getRelationshipDisplayText, getMeterTypeShortForm } from '../../../utils/meter.utils';

@Component({
  selector: 'app-meter-card-enhanced',
  templateUrl: './meter-card-enhanced.component.html',
  styleUrls: ['./meter-card-enhanced.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatDividerModule,
    MatBadgeModule,
    MatMenuModule
  ]
})
export class MeterCardEnhancedComponent {
  private _meter = signal<Meter | null>(null);
  
  @Input() 
  set meter(value: Meter) {
    this._meter.set(value);
  }
  get meter(): Meter {
    return this._meter()!;
  }
  
  @Input() selected: boolean = false;
  @Output() selectionChange = new EventEmitter<boolean>();
  @Output() share = new EventEmitter<Meter>();
  @Output() download = new EventEmitter<Meter>();
  @Output() withdrawConsent = new EventEmitter<Meter>();
  @Output() shareRevoked = new EventEmitter<void>();
  @Output() manageShares = new EventEmitter<Meter>();
  @Output() revokeShareRequest = new EventEmitter<ShareInfo>();

  displayableShares = computed<ShareInfo[]>(() => {
    const meter = this._meter();
    if (!meter?.shareeNames) return [];
    return meter.shareeNames.filter(s => s.shareType === 'platform');
  });

  onSelectionToggle(): void {
    this.selectionChange.emit(!this.selected);
  }

  onShare(): void {
    this.share.emit(this.meter);
  }

  onDownload(): void {
    this.download.emit(this.meter);
  }

  onWithdrawConsent(): void {
    this.withdrawConsent.emit(this.meter);
  }

  onManageShares(): void {
    this.manageShares.emit(this.meter);
  }

  // Expose utility functions for template
  getRelationshipDisplayText = getRelationshipDisplayText;
  getMeterTypeShortForm = getMeterTypeShortForm;

  private utilService = inject(UtilService);

  // Revoke a specific share
  revokeShare(share: ShareInfo): void {
    // Show confirmation popup
    this.utilService.confirmRevokeShare(share, this.meter.meterNumber).subscribe((confirmed: boolean) => {
      if (confirmed) {
        // User confirmed, emit event to parent component to handle revocation
        this.revokeShareRequest.emit(share);
      }
      // If user cancelled, do nothing
    });
  }

  getFuelTypeIcon(fuelType: string): string {
    return fuelType === 'Elec' ? 'bolt' : fuelType === 'Gas' ? 'local_fire_department' : 'device_unknown';
  }

  getFuelTypeClass(fuelType: string): string {
    return fuelType === 'Elec' ? 'elec-fuel' : fuelType === 'Gas' ? 'gas-fuel' : 'unknown-fuel';
  }

  getConsentStatusClass(status: ConsentStatus): string {
    switch (status) {
      case ConsentStatus.ACTIVE:
        return 'status-active';
      case ConsentStatus.EXPIRED:
        return 'status-expired';
      default:
        return 'status-unknown';
    }
  }
}